import { logger } from '../utils/logger';

export interface GeolocationError {
    code: number;
    message: string;
    userMessage: string;
}

export interface LocationServiceStatus {
    isSupported: boolean;
    hasPermission: boolean;
    isGPSEnabled: boolean;
    canRequestPermission: boolean;
    errorMessage?: string;
}

// Verifica se o GPS está habilitado e as permissões estão concedidas
export async function checkLocationServiceStatus(): Promise<LocationServiceStatus> {
    logger.log('🔍 Iniciando verificação do status da localização...');

    // Verifica se a API de geolocalização é suportada
    if (!navigator.geolocation) {
        logger.error('❌ Navegador não suporta geolocalização');
        return {
            isSupported: false,
            hasPermission: false,
            isGPSEnabled: false,
            canRequestPermission: false,
            errorMessage: 'Seu navegador não suporta geolocalização. Atualize para uma versão mais recente.'
        };
    }

    logger.log('✅ API de geolocalização suportada');

    try {
        // Verifica permissões usando a API Permissions (quando disponível)
        if ('permissions' in navigator) {
            logger.log('🔐 Verificando permissões via API Permissions...');
            const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
            logger.log('📋 Estado da permissão:', permission.state);

            if (permission.state === 'denied') {
                logger.error('❌ Permissão de localização negada');
                return {
                    isSupported: true,
                    hasPermission: false,
                    isGPSEnabled: false,
                    canRequestPermission: false,
                    errorMessage: 'Por favor, ative sua localização!'
                };
            }

            if (permission.state === 'granted') {
                logger.log('✅ Permissão concedida, testando GPS...');
                // Faz um teste rápido para verificar se o GPS está funcionando
                try {
                    await testGPSAvailability();
                    logger.log('✅ GPS funcionando corretamente');
                    return {
                        isSupported: true,
                        hasPermission: true,
                        isGPSEnabled: true,
                        canRequestPermission: false
                    };
                } catch (error: any) {
                    logger.error('❌ GPS não está funcionando:', error);
                    return {
                        isSupported: true,
                        hasPermission: true,
                        isGPSEnabled: false,
                        canRequestPermission: false,
                        errorMessage: getGPSErrorMessage(error.code)
                    };
                }
            }

            logger.log('⚠️ Permissão em estado prompt, prosseguindo com teste direto...');
        } else {
            logger.log('⚠️ API Permissions não disponível, fazendo teste direto...');
        }

        // Se não conseguir verificar permissões, faz um teste direto
        try {
            logger.log('🧪 Fazendo teste direto do GPS...');
            await testGPSAvailability();
            logger.log('✅ Teste direto do GPS bem-sucedido');
            return {
                isSupported: true,
                hasPermission: true,
                isGPSEnabled: true,
                canRequestPermission: true
            };
        } catch (error: any) {
            logger.error('❌ Teste direto falhou:', error);
            return {
                isSupported: true,
                hasPermission: error.code !== GeolocationPositionError.PERMISSION_DENIED,
                isGPSEnabled: error.code !== GeolocationPositionError.POSITION_UNAVAILABLE,
                canRequestPermission: error.code !== GeolocationPositionError.PERMISSION_DENIED,
                errorMessage: getGPSErrorMessage(error.code)
            };
        }
    } catch (error: any) {
        logger.error('❌ Erro inesperado na verificação:', error);
        return {
            isSupported: true,
            hasPermission: false,
            isGPSEnabled: false,
            canRequestPermission: true,
            errorMessage: `Erro ao verificar status da localização: ${error.message || 'Erro desconhecido'}`
        };
    }
}

// Testa rapidamente se o GPS está disponível
function testGPSAvailability(): Promise<GeolocationPosition> {
    logger.log('🧪 Iniciando teste de disponibilidade do GPS...');
    return new Promise((resolve, reject) => {
        const startTime = Date.now();

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const endTime = Date.now();
                logger.log(`✅ GPS respondeu em ${endTime - startTime}ms`);
                logger.log('📍 Posição obtida:', {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy
                });
                resolve(position);
            },
            (error) => {
                const endTime = Date.now();
                logger.error(`❌ GPS falhou após ${endTime - startTime}ms:`, {
                    code: error.code,
                    message: error.message
                });
                reject(error);
            },
            {
                enableHighAccuracy: true,
                timeout: 30000, // Aumentado de 5s para 30s para teste de disponibilidade
                maximumAge: 0
            }
        );
    });
}

// Detecta o tipo de dispositivo para orientações específicas
// function getDeviceType(): 'android' | 'ios' | 'windows' | 'mac' | 'unknown' {
//     const userAgent = navigator.userAgent.toLowerCase();
//     const platform = navigator.platform.toLowerCase();

//     if (/android/.test(userAgent)) return 'android';
//     if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';
//     if (/win/.test(platform)) return 'windows';
//     if (/mac/.test(platform)) return 'mac';

//     return 'unknown';
// }

// Gera instruções específicas para ativar GPS baseado no dispositivo
// function getGPSActivationInstructions(): string {
//     const deviceType = getDeviceType();

//     switch (deviceType) {
//         case 'android':
//             return '📱 Android - Para ativar GPS:\n\n' +
//                 '1. Abra "Configurações"\n' +
//                 '2. Toque em "Localização" ou "Local"\n' +
//                 '3. Ative o botão "Usar localização"\n' +
//                 '4. Certifique-se que "Precisão alta" está selecionado\n' +
//                 '5. Volte ao navegador e recarregue a página';

//         case 'ios':
//             return '📱 iPhone/iPad - Para ativar localização:\n\n' +
//                 '1. Abra "Ajustes"\n' +
//                 '2. Toque em "Privacidade e Segurança"\n' +
//                 '3. Toque em "Localização"\n' +
//                 '4. Ative "Localização"\n' +
//                 '5. Role para baixo e encontre seu navegador (Safari/Chrome)\n' +
//                 '6. Toque e selecione "Ao Usar o App"\n' +
//                 '7. Volte ao navegador e recarregue a página';

//         case 'windows':
//             return '💻 Windows - Para ativar localização:\n\n' +
//                 '1. Pressione Win + I para abrir Configurações\n' +
//                 '2. Clique em "Privacidade e segurança"\n' +
//                 '3. Clique em "Localização"\n' +
//                 '4. Ative "Localização para este dispositivo"\n' +
//                 '5. Ative "Permitir que aplicativos acessem sua localização"\n' +
//                 '6. Role para baixo e ative para seu navegador\n' +
//                 '7. Reinicie o navegador';

//         case 'mac':
//             return '💻 Mac - Para ativar localização:\n\n' +
//                 '1. Clique no menu Apple > "Preferências do Sistema"\n' +
//                 '2. Clique em "Segurança e Privacidade"\n' +
//                 '3. Clique na aba "Privacidade"\n' +
//                 '4. Selecione "Localização" na lista lateral\n' +
//                 '5. Clique no cadeado e digite sua senha\n' +
//                 '6. Marque a caixa ao lado do seu navegador\n' +
//                 '7. Reinicie o navegador';

//         default:
//             return '📱💻 Para ativar GPS/localização:\n\n' +
//                 '• Dispositivos móveis: Vá em Configurações > Localização\n' +
//                 '• Computadores: Vá em Configurações > Privacidade > Localização\n' +
//                 '• Ative o serviço de localização\n' +
//                 '• Permita acesso para o navegador\n' +
//                 '• Recarregue a página';
//     }
// }

// Gera mensagens de erro amigáveis baseadas no código de erro

function getGPSErrorMessage(errorCode: number): string {
    switch (errorCode) {
        case GeolocationPositionError.PERMISSION_DENIED:
            return 'Por favor, ative sua localização!';

        case GeolocationPositionError.POSITION_UNAVAILABLE:
            return 'Problema ao confirmar. \n' +
                '• Verifique sua rede.\n';

        case GeolocationPositionError.TIMEOUT:
            return 'Timeout ao obter localização. Para resolver:\n\n' +
                '⏱️ Soluções rápidas:\n' +
                '• Aguarde alguns segundos e tente novamente\n' +
                '• Verifique sua conexão com internet\n\n' +
                '🔄 Se persistir:\n' +
                '• Recarregue a página\n' +
                '• Reinicie o navegador\n';

        default:
            return 'Erro ao acessar GPS. Verificações:\n\n' +
                '✅ Checklist básico:\n' +
                '• Navegador tem as permissôes necessárias?\n' +
                '• Está conectado à internet?\n' +
                '🔧 Se nada funcionar:\n' +
                '• Reinicie o navegador\n' +
                '• Atualize o navegador\n' +
                '• Tente em outro navegador';
    }
}

// Função para tentar obter localização com configurações menos restritivas
async function tryGetLocationWithFallback(): Promise<GeolocationPosition> {
    const highAccuracyOptions = {
        enableHighAccuracy: true,
        timeout: 30000, // Aumentado de 15s para 30s
        maximumAge: 60000,
    };

    const lowAccuracyOptions = {
        enableHighAccuracy: false,
        timeout: 60000, // Aumentado de 30s para 60s
        maximumAge: 300000, // 5 minutos
    };

    try {
        // Primeira tentativa: alta precisão
        return await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, highAccuracyOptions);
        });
    } catch (error) {
        logger.log('Tentativa de alta precisão falhou, tentando com baixa precisão...');

        // Segunda tentativa: baixa precisão (mais tolerante)
        return await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, lowAccuracyOptions);
        });
    }
}

export function getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise(async (resolve, reject) => {
        // Primeiro verifica o status do serviço de localização
        const status = await checkLocationServiceStatus();

        if (!status.isSupported) {
            reject({
                code: 0,
                message: 'Geolocation not supported',
                userMessage: status.errorMessage || 'Seu navegador não suporta geolocalização'
            });
            return;
        }

        if (!status.hasPermission || !status.isGPSEnabled) {
            reject({
                code: status.hasPermission ? 2 : 1, // POSITION_UNAVAILABLE ou PERMISSION_DENIED
                message: status.hasPermission ? 'GPS disabled' : 'Permission denied',
                userMessage: status.errorMessage || 'Serviço de localização não está disponível'
            });
            return;
        }

        try {
            const position = await tryGetLocationWithFallback();
            resolve(position);
        } catch (error: any) {
            let userMessage = '';

            switch (error.code) {
                case error.PERMISSION_DENIED:
                    userMessage = 'Por favor, ative a localização.';
                    break;
                case error.POSITION_UNAVAILABLE:
                    userMessage = '• Aguarde alguns segundos\n' +
                        '• Verifique conexão Wi-Fi/dados';
                    break;
                case error.TIMEOUT:
                    userMessage = 'Tempo limite excedido. Verifique sua conexão e tente novamente.';
                    break;
                default:
                    userMessage = 'Erro desconhecido. Verifique as configurações do navegador.';
                    break;
            }

            reject({
                code: error.code,
                message: error.message,
                userMessage
            });
        }
    });
}
